import React, { useRef, useState } from 'react';
import { Box, Collapse, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ContentEntry } from '../../content/siteContent';
import { useTranslation } from '../../content/useTranslation';
import { COLORS, FONTS, TYPO } from '../../theme/designTokens';

interface LeistungenOverviewListProps {
  items: ContentEntry[];
  textColor: string;
  bodyColor: string;
}

/** Uniform blur on non-focus rows (desktop hover) */
const ACCORDION_BLUR_PX = 1;

const LeistungenOverviewList: React.FC<LeistungenOverviewListProps> = ({
  items,
  textColor,
  bodyColor,
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toggle = (i: number) => {
    if (openIndex === i) {
      setOpenIndex(null);
    } else {
      setOpenIndex(i);
    }
  };

  const getItemStyle = (i: number): React.CSSProperties => {
    if (isMobile || hoveredIndex === null) return {};

    const isInFocus = i === hoveredIndex || (openIndex !== null && i === openIndex);
    if (isInFocus) return { filter: 'none', opacity: 1 };

    return {
      filter: `blur(${ACCORDION_BLUR_PX}px)`,
      opacity: 0.9,
    };
  };

  const isItemHovered = (i: number) => hoveredIndex !== null && hoveredIndex === i;
  const hoverTextColor = '#2D2C2B';

  return (
    <Box onMouseLeave={() => setHoveredIndex(null)}>
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <Box
            key={item.slug}
            id={`leistung-${item.slug}`}
            onMouseEnter={() => setHoveredIndex(i)}
            style={getItemStyle(i)}
            sx={{
              transition: 'filter 0.3s ease, opacity 0.3s ease',
            }}
          >
            <Box
              sx={{
                height: '1px',
                backgroundColor: COLORS.btnBorderLight,
                width: '100%',
              }}
            />
            <Box
              ref={(el: HTMLDivElement | null) => { headerRefs.current[i] = el; }}
              onClick={() => toggle(i)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: { xs: '16px', md: '20px' },
                gap: '16px',
                cursor: 'pointer',
              }}
            >
              <Typography
                sx={{
                  ...TYPO.leistungTitle,
                  color: isItemHovered(i) ? hoverTextColor : textColor,
                  transition: 'color 0.3s ease',
                  flex: 1,
                  minWidth: 0,
                  pr: { md: '24px' },
                }}
              >
                {item.title}
              </Typography>

              {/* Mobile: +/x icon */}
              <Box
                component="span"
                aria-expanded={isOpen}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  p: 0,
                  flexShrink: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  fontSize: '24px',
                  fontWeight: 300,
                  color: isItemHovered(i) ? hoverTextColor : textColor,
                  transition: 'transform 0.3s ease, color 0.3s ease',
                  transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                }}
              >
                +
              </Box>

              {/* Desktop: Mehr erfahren text on hover */}
              <Typography
                sx={{
                  display: { xs: 'none', md: 'inline' },
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  fontFamily: FONTS.sans,
                  fontWeight: 300,
                  fontSize: '13px',
                  letterSpacing: '1.6px',
                  textTransform: 'uppercase',
                  color: bodyColor,
                  opacity: !isOpen && isItemHovered(i) ? 1 : 0,
                  transition: 'opacity 0.25s ease',
                  pointerEvents: 'none',
                }}
              >
                {t.mehrErfahren}
              </Typography>
            </Box>
            <Collapse in={isOpen} timeout={350}>
              <Box sx={{ maxWidth: { xs: 'calc(100% - 48px)', md: 640 }, pb: { xs: '20px', md: '28px' } }}>
                {item.body?.split('\n\n').filter(Boolean).map((p, pi) => (
                  <Typography
                    key={pi}
                    sx={{
                      ...TYPO.bodyLight,
                      color: bodyColor,
                      mb: '16px',
                    }}
                  >
                    {p}
                  </Typography>
                ))}
              </Box>
            </Collapse>
            {i === items.length - 1 && (
              <Box
                sx={{
                  height: '1px',
                  backgroundColor: COLORS.btnBorderLight,
                  width: '100%',
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default LeistungenOverviewList;
